import {
  cardLabel,
  SUIT_NAMES,
  type FreecellGameEvent,
  type GameEvent,
  type MinesweeperGameEvent,
  type NoughtsGameEvent,
  type SolitaireGameEvent,
  type SpiderGameEvent,
} from '@cardengine/shared';

export type EventTone = 'plain' | 'good' | 'bad' | 'note';

export interface NarratedEvent {
  text: string;
  tone: EventTone;
  /** Worth interrupting the player with a toast, not just logging. */
  notable: boolean;
}

export interface Narrator {
  /** Display name, or "You". */
  name: (playerId: string) => string;
}

/**
 * Turns an engine event into a sentence.
 *
 * The engine emits structured facts; this is the only place that decides how
 * they read. Both the toasts and the game log go through it, so a message can
 * never say one thing in one place and something else in another.
 */
export function narrate(event: GameEvent, who: Narrator, viewerId: string | null): NarratedEvent | null {
  const name = (id: string) => who.name(id);
  const isYou = (id: string) => id === viewerId;
  // "You picks up" would be wrong, so verbs agree with the subject.
  const verb = (id: string, second: string, third: string) => (isYou(id) ? second : third);

  switch (event.type) {
    case 'gameStarted':
      return { text: `Cards dealt — ${event.handSize} each.`, tone: 'note', notable: false };

    case 'cardsPlayed': {
      const cards = event.cards.map(cardLabel).join(' ');
      return {
        text: `${name(event.playerId)} ${verb(event.playerId, 'played', 'played')} ${cards}`,
        tone: 'plain',
        notable: false,
      };
    }

    case 'cardsDrawn': {
      if (event.reason === 'penalty') {
        // A pick-up normally leaves your go intact, so spell out the exception.
        const stuck = event.endedTurn ? ' — nothing to play' : '';
        return {
          text: `${name(event.playerId)} ${verb(event.playerId, 'pick up', 'picks up')} ${event.count}${stuck}`,
          tone: isYou(event.playerId) ? 'bad' : 'note',
          notable: true,
        };
      }
      const noun = event.count === 1 ? 'a card' : `${event.count} cards`;
      return {
        text: `${name(event.playerId)} ${verb(event.playerId, 'drew', 'drew')} ${noun}`,
        tone: 'plain',
        notable: false,
      };
    }

    case 'suitChosen':
      return {
        text: `${name(event.playerId)} ${verb(event.playerId, 'changed', 'changed')} the suit to ${SUIT_NAMES[event.suit]}`,
        tone: 'note',
        notable: true,
      };

    case 'playerSkipped':
      return {
        text: `${name(event.playerId)} ${verb(event.playerId, 'miss', 'misses')} a turn`,
        tone: isYou(event.playerId) ? 'bad' : 'note',
        notable: true,
      };

    case 'penaltyRaised':
      return {
        text: event.opened
          ? `${name(event.playerId)} ${verb(event.playerId, 'start', 'starts')} a pick-up of ${event.total}`
          : `${name(event.playerId)} ${verb(event.playerId, 'raise', 'raises')} the pick-up to ${event.total}`,
        tone: 'note',
        notable: true,
      };

    case 'penaltyBlocked':
      return {
        text: `${name(event.playerId)} ${verb(event.playerId, 'blocked', 'blocked')} the pick-up of ${event.amount}`,
        tone: 'good',
        notable: true,
      };

    case 'playsAgain':
      return {
        text: `${name(event.playerId)} ${verb(event.playerId, 'go', 'goes')} again`,
        tone: 'note',
        notable: true,
      };

    case 'directionReversed':
      return { text: 'Direction of play reversed', tone: 'note', notable: true };

    case 'lastCardDeclared':
      return {
        text: `${name(event.playerId)} ${verb(event.playerId, 'called', 'called')} last card!`,
        tone: 'note',
        notable: true,
      };


    case 'playerFinished':
      return {
        text: `${name(event.playerId)} ${verb(event.playerId, 'went', 'went')} out`,
        tone: 'good',
        notable: true,
      };

    case 'deckReshuffled':
      return { text: 'Draw pile ran out — discards reshuffled', tone: 'note', notable: true };

    case 'passed':
      return {
        text: `${name(event.playerId)} ${verb(event.playerId, 'passed', 'passed')}`,
        tone: 'plain',
        notable: false,
      };

    // Turn changes are obvious from the table, and the result screen covers the
    // end of the game, so neither needs a line of its own.
    case 'turnStarted':
    case 'gameOver':
      return null;

    default:
      return null;
  }
}

/**
 * The Solitaire equivalent.
 *
 * Kept as its own function rather than another arm of `narrate`: the two games
 * share no event names, and folding them together would mean one `switch` where
 * half the cases are unreachable for whichever game is actually being played.
 */
export function narrateSolitaire(event: SolitaireGameEvent): NarratedEvent | null {
  switch (event.type) {
    case 'gameStarted':
      return {
        text: `Fresh deal — turning ${event.drawCount === 1 ? 'one card' : 'three cards'} at a time.`,
        tone: 'note',
        notable: false,
      };

    case 'foundationAdvanced':
      return { text: `${event.rank} of ${SUIT_NAMES[event.suit]} banked`, tone: 'good', notable: false };

    case 'cardRevealed':
      return { text: `Turned up ${cardLabel(event.card)}`, tone: 'plain', notable: false };

    case 'wasteRecycled':
      return { text: `Turned the pile back over (${event.count} cards)`, tone: 'note', notable: false };

    case 'gameWon':
      return { text: `Solved in ${event.moves} moves!`, tone: 'good', notable: true };

    case 'gameStuck':
      return { text: 'No moves left — that deal is dead.', tone: 'bad', notable: true };

    // Individual draws and moves are plain to see on the board; logging every
    // one would bury the handful of lines that actually matter.
    case 'cardsDrawn':
    case 'cardsMoved':
      return null;

    default:
      return null;
  }
}

/**
 * And FreeCell's.
 *
 * Its own function for the same reason Solitaire's is: the engines share no
 * event names, and the two that look alike — `gameStarted`, `gameStuck` — say
 * different things, because a FreeCell deal that dies did so with every card
 * in plain sight.
 */
export function narrateFreecell(event: FreecellGameEvent): NarratedEvent | null {
  switch (event.type) {
    case 'gameStarted':
      return {
        text: `Fresh deal — everything face up, ${event.freeCells} ${
          event.freeCells === 1 ? 'cell' : 'cells'
        } to work with.`,
        tone: 'note',
        notable: false,
      };

    case 'foundationAdvanced':
      return { text: `${event.rank} of ${SUIT_NAMES[event.suit]} banked`, tone: 'good', notable: false };

    case 'gameWon':
      return { text: `Solved in ${event.moves} moves!`, tone: 'good', notable: true };

    case 'gameStuck':
      return {
        text: 'Every cell full and nothing will move — that one is dead.',
        tone: 'bad',
        notable: true,
      };

    // Moves are plain to see on the board, and there are a great many of them.
    case 'cardsMoved':
      return null;

    default:
      return null;
  }
}

/**
 * And Spider's.
 *
 * The one that earns its narration is `sequenceCompleted`. Thirteen cards
 * vanishing off a column is the biggest thing that happens in Spider and the
 * whole point of the game, and unlike the other two patience games it is
 * progress you want counted out loud — eight of them and you are home.
 */
export function narrateSpider(event: SpiderGameEvent): NarratedEvent | null {
  switch (event.type) {
    case 'gameStarted':
      return {
        text: `Fresh deal — two packs, ${event.suitCount === 1 ? 'one suit' : `${event.suitCount} suits`}.`,
        tone: 'note',
        notable: false,
      };

    case 'sequenceCompleted':
      return {
        text: `${SUIT_NAMES[event.suit]} run complete — ${event.total} of 8 home`,
        tone: 'good',
        notable: true,
      };

    case 'stockDealt':
      return {
        text:
          event.dealsLeft > 0
            ? `Dealt a row — ${event.dealsLeft} ${event.dealsLeft === 1 ? 'deal' : 'deals'} left`
            : 'Dealt the last row — that is the whole pack on the table',
        tone: 'note',
        notable: false,
      };

    case 'cardRevealed':
      return { text: `Turned up ${cardLabel(event.card)}`, tone: 'plain', notable: false };

    case 'gameWon':
      return { text: `All eight runs home in ${event.moves} moves!`, tone: 'good', notable: true };

    case 'gameStuck':
      return { text: 'Nothing stacks and the stock is spent — that deal is done.', tone: 'bad', notable: true };

    // Individual moves are plain to see, and Spider generates hundreds.
    case 'cardsMoved':
      return null;

    default:
      return null;
  }
}

/**
 * And noughts and crosses'.
 *
 * The first narrator on the site with an opponent but no cards, which changes
 * what is worth saying. In a card game the log exists because play is fast and
 * hands are hidden; here the whole board is on screen and every mark is
 * visible, so narrating each one would just be noise beside the grid. What the
 * log is for is the things the board cannot show on its own: who started, and
 * how it ended — including the misère ending, which looks identical to a win
 * on the board and is the exact opposite of one.
 */
export function narrateNoughts(event: NoughtsGameEvent, who: Narrator, viewerId: string | null): NarratedEvent | null {
  const isYou = (id: string) => id === viewerId;

  switch (event.type) {
    case 'gameStarted': {
      const grid = `${event.boardSize}×${event.boardSize}`;
      const goal = event.misere
        ? `avoid ${event.winLength} in a row`
        : `${event.winLength} in a row`;
      const opener = isYou(event.firstPlayerId) ? 'You go' : `${who.name(event.firstPlayerId)} goes`;
      return { text: `${grid} grid, ${goal}. ${opener} first.`, tone: 'note', notable: false };
    }

    case 'gameWon': {
      const youWon = isYou(event.playerId);
      // Under misère nobody made a winning line — somebody made a losing one.
      const text = event.misere
        ? youWon
          ? 'They made a line. You win!'
          : `You made a line — ${who.name(event.playerId)} wins.`
        : youWon
          ? 'You win!'
          : `${who.name(event.playerId)} wins.`;
      return { text, tone: youWon ? 'good' : 'bad', notable: true };
    }

    case 'gameDrawn':
      return { text: 'A draw — the grid is full.', tone: 'note', notable: true };

    // Every mark is already on the board, in front of both players. Repeating
    // it in the log would say nothing the grid has not already said.
    case 'marked':
    case 'turnStarted':
      return null;

    default:
      return null;
  }
}

/**
 * And for Minesweeper — which mostly declines to say anything.
 *
 * The other narrators exist because a card that has moved is gone from where it
 * was: unless the log records it, the board no longer shows what happened. A
 * Minesweeper board is the opposite — every square you have opened stays open
 * in front of you, so a line saying "you opened eleven squares" is describing
 * something the player is looking at.
 *
 * What is worth saying is what the grid cannot show. Running out of flags looks
 * like nothing at all — the square simply does not take the mark. And both
 * endings deserve a sentence, because the difference between a board you read
 * out and a board you guessed at is the only thing worth telling somebody about
 * afterwards.
 */
export function narrateMinesweeper(event: MinesweeperGameEvent): NarratedEvent | null {
  switch (event.type) {
    case 'gameStarted':
      return {
        text: `${event.width}×${event.height}, ${event.mines} mines. First square is always safe.`,
        tone: 'note',
        notable: false,
      };

    case 'flagged':
      // Only the last one. Every other flag is visible on the square itself.
      return event.flagsLeft === 0
        ? { text: 'That is the last flag. Any more, and one of them is wrong.', tone: 'note', notable: true }
        : null;

    case 'gameWon':
      return { text: `Board cleared, in ${event.moves} moves!`, tone: 'good', notable: true };

    case 'gameLost': {
      const short = event.safe - event.cleared;
      return {
        text:
          event.cleared === 0
            ? 'Straight onto a mine. That one was luck, not judgement.'
            : `Mine. ${short} ${short === 1 ? 'square' : 'squares'} short of clearing it.`,
        tone: 'bad',
        notable: true,
      };
    }

    // The board itself is the record of these.
    case 'opened':
    case 'unflagged':
      return null;

    default:
      return null;
  }
}
